<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Ad Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('TABLES.AcceptedAds.ad_name')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Ad Name Input -->

              <!-- Start:: Advertiser Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('TABLES.AcceptedAds.advertiser_name')"
                v-model.trim="filterOptions.advertiser_name"
              />
              <!-- End:: Advertiser Name Input -->

              <!-- Start:: Shop Type Select -->
              <base-select-input
                col="3"
                :optionsList="shopTypes"
                :placeholder="$t('TABLES.AcceptedAds.shop_type')"
                v-model="filterOptions.shop_type"
              />
              <!-- End:: Shop Type Select -->

              <!-- Start:: Shop Status Select -->
              <base-select-input
                col="3"
                :optionsList="shopStatuses"
                :placeholder="$t('PLACEHOLDERS.shop_status')"
                v-model="filterOptions.shop_status"
              />
              <!-- End:: Shop Status Select -->

              <!-- Start:: Active Status Select -->
              <base-select-input
                col="3"
                :optionsList="activeStatuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.is_active"
              />
              <!-- End:: Active Status Select -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TABLES.AcceptedAds.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Ad Name -->
        <template v-slot:[`item.name`]="{ item }">
          <span class="text-danger" v-if="!item.name">
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.name }} </span>
        </template>
        <!-- End:: Ad Name -->

        <!-- Start:: Advertiser Name -->
        <template v-slot:[`item.advertiser_name`]="{ item }">
          <span class="text-danger" v-if="!item.user || !item.user.user">
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.user.user.name }} </span>
        </template>
        <!-- End:: Advertiser Name -->

        <!-- Start:: Shop Type -->
        <template v-slot:[`item.shop_type`]="{ item }">
          <span> {{ item._shop_type || "-" }} </span>
        </template>
        <!-- End:: Shop Type -->

        <!-- Start:: Created At -->
        <template v-slot:[`item.created_at`]="{ item }">
          <span> {{ item.created_at || "-" }} </span>
        </template>
        <!-- End:: Created At -->

        <!-- Start:: Activation Status -->
        <template v-slot:[`item.is_active`]="{ item }">
          <span class="text-success text-h5" v-if="item.is_active">
            <i class="far fa-check"></i>
          </span>
          <span class="text-danger text-h5" v-else>
            <i class="far fa-times"></i>
          </span>
        </template>
        <!-- End:: Activation Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Show Button -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item.id)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <!-- Edit Button -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>
              <button class="btn_edit" @click="editItem(item.id)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>

            <!-- Toggle Activation Button -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{
                  item.is_active
                    ? $t("BUTTONS.deactivate")
                    : $t("BUTTONS.activate")
                }}</span>
              </template>
              <button
                :class="item.is_active ? 'btn_delete' : 'btn_success'"
                @click="toggleActivation(item)"
              >
                <i :class="item.is_active ? 'fal fa-ban' : 'fal fa-check'"></i>
              </button>
            </a-tooltip>

            <!-- Delete Button -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="openDeleteModal(item)">
                <i class="fal fa-trash"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Pagination =========== -->
      <div class="pagination_container">
        <v-pagination
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          color="#404b3e"
        ></v-pagination>
      </div>
      <!--  =========== End:: Pagination =========== -->
    </main>

    <!-- Start:: Delete Reason Modal -->
    <v-dialog v-model="deleteModalIsOpen" max-width="500">
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("TITLES.delete_reason") }}
        </v-card-title>
        <v-card-text>
          <base-input
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.delete_reason')"
            v-model.trim="deleteReason"
            required
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            class="modal_confirm_btn"
            @click="confirmDelete"
            :disabled="!deleteReason || isWaitingRequest"
          >
            {{ $t("BUTTONS.confirm") }}
          </v-btn>
          <v-btn
            class="modal_cancel_btn"
            @click="closeDeleteModal"
            :disabled="isWaitingRequest"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
      
    </v-dialog>
    <!-- End:: Delete Reason Modal -->
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "AllAcceptedAds",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        advertiser_name: null,
        shop_type: null,
        shop_status: null,
        is_active: null,
      },
      shopTypes: [
        {
          id: "shack",
          name: this.$t("TABLES.AcceptedAds.shack"),
        },
        {
          id: "exposition",
          name: this.$t("TABLES.AcceptedAds.exposition"),
        },
      ],
      shopStatuses: [
        {
          id: "ready",
          name: this.$t("TABLES.AcceptedAds.ready"),
        },
        {
          id: "unfinished",
          name: this.$t("TABLES.AcceptedAds.unfinished"),
        },
        {
          id: "land",
          name: this.$t("TABLES.AcceptedAds.land"),
        },
      ],
      activeStatuses: [
        {
          id: 1,
          name: this.$t("TABLES.AcceptedAds.active"),
        },
        {
          id: 0,
          name: this.$t("TABLES.AcceptedAds.inactive"),
        },
      ],
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("TABLES.AcceptedAds.serial"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AcceptedAds.ad_name"),
          value: "name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AcceptedAds.advertiser_name"),
          value: "advertiser_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AcceptedAds.shop_type"),
          value: "shop_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AcceptedAds.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AcceptedAds.status"),
          value: "is_active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.AcceptedAds.actions"),
          value: "actions",
          align: "center",
          width: "200",
          sortable: false,
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data

      // Start:: Delete Modal Data
      deleteModalIsOpen: false,
      selectedItem: null,
      deleteReason: "",
      // End:: Delete Modal Data
    };
  },

  methods: {
    // Start:: Vuex Actions
    ...mapActions({
      updateAppLoadingStatus: "LoadingModule/updateAppLoadingStatus",
    }),
    // End:: Vuex Actions

    // Start:: Get All Data
    async getAllData() {
      this.loading = true;
      this.updateAppLoadingStatus(true);

      try {
        let res = await this.$axios({
          method: "GET",
          url: "ads",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.name,
            advertiser_name: this.filterOptions.advertiser_name,
            shop_type: this.filterOptions.shop_type?.id,
            shop_status: this.filterOptions.shop_status?.id,
            is_active: this.filterOptions.is_active?.id,
          },
        });
        this.loading = false;
        this.updateAppLoadingStatus(false);
        this.tableRows = res.data.data.data;
        this.paginations.current_page = res.data.data.meta.current_page;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        this.updateAppLoadingStatus(false);
        console.log(error.response.data.message);
      }
    },
    // End:: Get All Data

    // Start:: Submit Filter Form
    submitFilterForm() {
      this.paginations.current_page = 1;
      this.getAllData();
    },
    // End:: Submit Filter Form

    // Start:: Reset Filter
    resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.advertiser_name = null;
      this.filterOptions.shop_type = null;
      this.filterOptions.shop_status = null;
      this.filterOptions.is_active = null;
      this.paginations.current_page = 1;
      this.getAllData();
    },
    // End:: Reset Filter

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      this.paginations.current_page = pagenationValue;
      this.getAllData();
    },
    // End:: Update Router Query Param

    // Start:: Show Item
    showItem(id) {
      this.$router.push({ path: `/accepted-ads/show/${id}` });
    },
    // End:: Show Item

    // Start:: Edit Item
    editItem(id) {
      this.$router.push({ path: `/accepted-ads/edit/${id}` });
    },
    // End:: Edit Item

    // Start:: Toggle Activation
    async toggleActivation(item) {
      this.isWaitingRequest = true;
      this.updateAppLoadingStatus(true);

      try {
        let res = await this.$axios({
          method: "POST",
          url: `ads/${item.id}/toggle-active`,
        });

        this.$message.success(res.data.message);

        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.getAllData();
      } catch (error) {
        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.failed")
        );
      }
    },
    // End:: Toggle Activation

    // Start:: Open Delete Modal
    openDeleteModal(item) {
      this.selectedItem = item;
      this.deleteReason = "";
      this.deleteModalIsOpen = true;
    },
    // End:: Open Delete Modal

    // Start:: Close Delete Modal
    closeDeleteModal() {
      this.deleteModalIsOpen = false;
      this.selectedItem = null;
      this.deleteReason = "";
    },
    // End:: Close Delete Modal

    // Start:: Confirm Delete
    async confirmDelete() {
      if (!this.deleteReason) {
        this.$message.error(this.$t("MESSAGES.delete_reason_required"));
        return;
      }

      this.isWaitingRequest = true;
      this.updateAppLoadingStatus(true);

      try {
        await this.$axios({
          method: "DELETE",
          url: `ads/${this.selectedItem.id}`,
          data: {
            reason: this.deleteReason,
          },
        });

        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));

        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.closeDeleteModal();
        this.getAllData();
      } catch (error) {
        this.isWaitingRequest = false;
        this.updateAppLoadingStatus(false);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.failed")
        );
      }
    },
    // End:: Confirm Delete
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = Number(this.$route.query.page);
    }
    this.getAllData();
    // End:: Fire Methods
  },
};
</script>
